const buildTempId = (() => {
  let counter = 0;
  return () => `temp-promocion-${counter++}`;
})();

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const [datePart] = value.split("T");
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || "";
  return date.toISOString().split("T")[0];
};

const normalizeText = (value) =>
  typeof value === "string"
    ? value.trim().toLowerCase()
    : value
      ? String(value).trim().toLowerCase()
      : "";

export const normalizePromocion = (promocion = {}, fallbackId) => {
  const fechaInicioRaw = promocion.fecha_inicio ?? promocion.fechaInicio ?? "";
  const fechaFinRaw = promocion.fecha_fin ?? promocion.fechaFin ?? "";

  return {
    id: promocion.id ?? fallbackId ?? buildTempId(),
    nombre:
      promocion.numero_promocion?.trim() ||
      promocion.nombre?.trim() ||
      "Sin numero",
    cicloId: promocion.ciclo_id ?? promocion.cicloId ?? null,
    cicloCodigo: promocion.ciclo_codigo?.trim() || promocion.cicloCodigo?.trim() || "",
    cicloAnio: promocion.ciclo_anio ?? promocion.cicloAnio ?? null,
    cicloNombre: promocion.ciclo_nombre?.trim() || promocion.cicloNombre?.trim() || "",
    codigoConvocatoria: promocion.codigo_convocatoria?.trim() || promocion.codigoConvocatoria?.trim() || "",
    cursoId: promocion.curso_id ?? promocion.cursoId ?? null,
    horarioId: promocion.horario_id ?? promocion.horarioId ?? null,
    cursoNombre: promocion.curso?.trim() || promocion.cursoNombre?.trim() || "",
    centroNombre: promocion.centro?.trim() || promocion.centroNombre?.trim() || "",
    fechaInicio: toDateInputValue(fechaInicioRaw),
    fechaFin: toDateInputValue(fechaFinRaw),
    capacidad:
      promocion.capacidad !== undefined && promocion.capacidad !== null
        ? Number(promocion.capacidad)
        : null,
    matriculados:
      promocion.matriculados !== undefined && promocion.matriculados !== null
        ? Number(promocion.matriculados)
        : null,
    cuposDisponibles:
      promocion.cupos_disponibles !== undefined && promocion.cupos_disponibles !== null
        ? Number(promocion.cupos_disponibles)
        : promocion.cuposDisponibles !== undefined && promocion.cuposDisponibles !== null
          ? Number(promocion.cuposDisponibles)
          : null,
    estado: promocion.estado || "programado",
    descuentoPorcentaje:
      promocion.descuento_porcentaje !== undefined &&
      promocion.descuento_porcentaje !== null
        ? Number(promocion.descuento_porcentaje)
        : promocion.descuentoPorcentaje !== undefined &&
            promocion.descuentoPorcentaje !== null
            ? Number(promocion.descuentoPorcentaje)
            : null,
    totalSesiones:
      promocion.total_sesiones !== undefined && promocion.total_sesiones !== null
        ? Number(promocion.total_sesiones)
        : 0,
    linkWhatsapp: promocion.link_whatsapp || promocion.linkWhatsapp || "",
    activo:
      typeof promocion.activo === "boolean"
        ? promocion.activo
        : promocion.activo === 1 || promocion.activo === "1",
    createdAt: promocion.created_at || promocion.createdAt || null,
  };
};

export const resolveCursoRelacion = (promocion, cursos = []) => {
  if (!promocion) return {};
  const directId = promocion.curso_id ?? promocion.cursoId;
  if (directId) {
    const curso = cursos.find((item) => String(item.id) === String(directId));
    return { cursoId: String(directId), curso };
  }

  const promoCursoNombre = normalizeText(
    promocion.curso_nombre || promocion.cursoNombre || promocion.curso
  );
  if (!promoCursoNombre) return {};
  const promoCentroNombre = normalizeText(
    promocion.centro_nombre || promocion.centroNombre || promocion.centro
  );

  const curso = cursos.find((item) => {
    if (normalizeText(item.nombre) !== promoCursoNombre) return false;
    if (!promoCentroNombre) return true;
    const centroActual = normalizeText(
      item.centroNombre || item.centro || item.centro_educativo
    );
    return centroActual === promoCentroNombre;
  });

  return curso ? { cursoId: String(curso.id), curso } : {};
};

export const enrichPromocionWithCurso = (promocion, cursos = []) => {
  const { cursoId, curso } = resolveCursoRelacion(promocion, cursos);
  if (!cursoId && !curso) return promocion;
  return {
    ...promocion,
    cursoId: promocion.cursoId ?? (cursoId ? Number(cursoId) : null),
    cursoNombre: promocion.cursoNombre || curso?.nombre || "",
    centroNombre:
      promocion.centroNombre ||
      curso?.centroNombre ||
      curso?.centro ||
      curso?.centro_educativo ||
      "",
  };
};

export const initialFormState = {
  numero_promocion: "",
  ciclo_id: "",
  curso_id: "",
  horario_id: "",
  fecha_inicio: "",
  fecha_fin: "",
  capacidad: 25,
  estado: "programado",
  descuento_porcentaje: "",
  link_whatsapp: "",
  activo: true,
};

export const buildPayload = (form) => ({
  numero_promocion: form.numero_promocion?.trim() || "",
  ciclo_id: form.ciclo_id ? Number(form.ciclo_id) : null,
  curso_id: form.curso_id ? Number(form.curso_id) : null,
  horario_id: form.horario_id ? Number(form.horario_id) : null,
  fecha_inicio: form.fecha_inicio || null,
  fecha_fin: form.fecha_fin || null,
  capacidad:
    form.capacidad !== "" && form.capacidad !== null ? Number(form.capacidad) : null,
  estado: form.estado || "programado",
  descuento_porcentaje:
    form.descuento_porcentaje !== "" && form.descuento_porcentaje !== null
      ? Number(form.descuento_porcentaje)
      : null,
  link_whatsapp: form.link_whatsapp?.trim() || null,
  activo: form.activo ? 1 : 0,
});

export const validarPromocion = (form) => {
  if (!form.numero_promocion?.trim()) return "El numero de la promocion es obligatorio.";
  if (!form.ciclo_id) return "Selecciona el ciclo.";
  if (!form.curso_id) return "Selecciona el curso asociado.";
  if (!form.horario_id) return "Selecciona el horario de la promocion.";
  if (
    form.descuento_porcentaje === "" ||
    form.descuento_porcentaje === null ||
    Number.isNaN(Number(form.descuento_porcentaje))
  )
    return "Ingresa el descuento (%) de la promocion.";
  if (!form.fecha_inicio) return "Selecciona la fecha de inicio.";
  return null;
};

export const extractPromocion = (response, fallback) => {
  const base = response?.promocion ?? response ?? {};
  const merged = fallback ? { ...fallback, ...base } : base;
  return normalizePromocion(merged);
};
